import { useEffect, useRef, useState } from 'react';

/**
 * Count-up number animation. Runs once on mount, then stays at the target.
 * Content is always rendered (no hidden frames).
 */
export default function AnimatedNumber({ value = 0, duration = 900, suffix = '', prefix = '', className = '' }) {
    const [display, setDisplay] = useState(Number(value) || 0);
    const hasAnimatedRef = useRef(false);
    const fromRef = useRef(0);

    useEffect(() => {
        const target = Number(value) || 0;
        const from = hasAnimatedRef.current ? fromRef.current : 0;
        hasAnimatedRef.current = true;

        let raf;
        let start = null;
        const step = (ts) => {
            if (start === null) start = ts;
            const t = Math.min(1, (ts - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            const current = from + (target - from) * eased;
            setDisplay(current);
            if (t < 1) {
                raf = requestAnimationFrame(step);
            } else {
                fromRef.current = target;
            }
        };
        raf = requestAnimationFrame(step);
        return () => raf && cancelAnimationFrame(raf);
    }, [value, duration]);

    const formatted = Number.isInteger(value)
        ? Math.round(display).toLocaleString('nl-NL')
        : display.toFixed(1);

    return (
        <span className={className} data-testid="animated-number">
            {prefix}{formatted}{suffix}
        </span>
    );
}
